import React, { useMemo } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import dayjs from "../../common/date";
import ListHeader from "../../common/components/list-header";
import { backgrounds, borders, tableColors } from "../../theme/theme";
import { Engagement, Timesheet } from "../types";

interface MissingEntry {
  engagement: Engagement;
  missingPeriods: string[];
}

function getCheckMonth(today: Date): { year: number; month: number } {
  if (today.getDate() <= 5) {
    const prev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    return { year: prev.getFullYear(), month: prev.getMonth() };
  }
  return { year: today.getFullYear(), month: today.getMonth() };
}

interface Props {
  engagements: Engagement[];
  timesheets: Timesheet[];
}

function AdminMissingTimesheets({ engagements, timesheets }: Props) {
  const today = useMemo(() => new Date(), []);

  const { missingEntries, checkMonthLabel } = useMemo(() => {
    if (!engagements?.length || !timesheets?.length) {
      return { missingEntries: [] as MissingEntry[], checkMonthLabel: "" };
    }

    const { year, month } = getCheckMonth(today);
    const label = new Date(year, month, 1).toLocaleString("default", {
      month: "long",
      year: "numeric",
    });

    const monthEnd = new Date(year, month + 1, 0); // last day, local time
    const period1End = new Date(year, month, 15, 23, 59, 59); // local time

    const period1Ended = today >= period1End;
    const period2Ended = today >= monthEnd;

    // UTC boundaries for comparing server date strings
    const monthStr = `${year}-${String(month + 1).padStart(2, "0")}`;
    const utcMonthStart = dayjs.utc(`${monthStr}-01`);
    const utcMonthEnd = utcMonthStart.endOf("month");

    const activeEngagements = engagements.filter((e) => {
      if (e.endDate) return false;
      if (e.startDate && dayjs.utc(e.startDate).isAfter(utcMonthEnd)) return false;
      return true;
    });

    const missing: MissingEntry[] = [];

    for (const engagement of activeEngagements) {
      const submitted = timesheets.filter(
        (t) => t.engagement.id === engagement.id && t.submittedAt
      );

      const hasPeriod1 = submitted.some((t) => {
        const end = dayjs.utc(t.endDate);
        return end.year() === year && end.month() === month && end.date() <= 15;
      });

      const hasPeriod2 = submitted.some((t) => {
        const end = dayjs.utc(t.endDate);
        return end.year() === year && end.month() === month && end.date() >= 16;
      });

      const missingPeriods: string[] = [];
      if (!hasPeriod1 && period1Ended) missingPeriods.push(`1–15`);
      if (!hasPeriod2 && period2Ended) missingPeriods.push(`16–${monthEnd.getDate()}`);

      if (missingPeriods.length > 0) {
        missing.push({ engagement, missingPeriods });
      }
    }

    missing.sort((a, b) =>
      a.engagement.user.lastName.localeCompare(b.engagement.user.lastName)
    );

    return { missingEntries: missing, checkMonthLabel: label };
  }, [engagements, timesheets, today]);

  if (!missingEntries.length) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Accordion
        defaultExpanded
        elevation={0}
        sx={{
          border: `1px solid ${borders.main}`,
          borderRadius: "8px !important",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "#888" }} />}
          sx={{
            backgroundColor: backgrounds.surface,
            borderRadius: "8px 8px 0 0",
            "&.Mui-expanded": { borderRadius: "8px 8px 0 0" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <WarningAmberIcon sx={{ color: "#aaa", fontSize: 18 }} />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#666" }}
            >
              Missing Submissions — {checkMonthLabel}
            </Typography>
            <Chip
              label={missingEntries.length}
              size="small"
              sx={{
                backgroundColor: "#c5d5eb",
                color: "#444",
                fontWeight: 700,
                height: 20,
                fontSize: "0.75rem",
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Table size="small">
            <ListHeader
              columns={[
                { name: "Developer Name" },
                { name: "Client" },
                { name: "Missing Periods" },
              ]}
            />
            <TableBody>
              {missingEntries.map(({ engagement, missingPeriods }, i) => (
                <TableRow
                  key={engagement.id}
                  sx={{
                    backgroundColor:
                      i % 2 === 0 ? tableColors.rowEven : tableColors.rowOdd,
                  }}
                >
                  <TableCell>
                    {engagement.user.firstName} {engagement.user.lastName}
                  </TableCell>
                  <TableCell>{engagement.clientName}</TableCell>
                  <TableCell>
                    {missingPeriods.map((p) => (
                      <Chip
                        key={p}
                        label={p}
                        size="small"
                        sx={{
                          mr: 0.5,
                          backgroundColor: "#e8eef5",
                          color: "#555",
                          fontWeight: 600,
                          fontSize: "0.72rem",
                        }}
                      />
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default AdminMissingTimesheets;
